import { useEffect, useState, type ReactNode } from "react";
import { Navigate, NavLink, useNavigate, useParams } from "react-router-dom";

import {
  useArchiveCollection,
  useCollection,
  useCollectionMembers,
  useDeleteCollection,
  useDuplicateCollection,
} from "../api/collections";
import { useDocumentPage, type DocumentListBody } from "../api/documents";
import { collectionPath } from "../api/queries";
import type { Collection, User } from "../api/types";
import { granted } from "../auth/AbilityGate";
import { useAuth } from "../auth/auth-context";
import { DocumentList } from "../documents/DocumentList";
import { ShareDialog } from "../documents/ShareDialog";
import { LazyMarkdown } from "../documents/LazyMarkdown";
import { PageHeader } from "../layout/PageHeader";
import { cx } from "../lib/cx";
import { StorageKey, readStored, writeStored } from "../lib/storage";
import { Button } from "../ui/Button";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { Menu, type MenuItem } from "../ui/Menu";
import { useToast } from "../ui/toast-context";
import { CollectionPermissionsDialog } from "./CollectionPermissionsDialog";
import { EditCollectionDialog } from "./EditCollectionDialog";

/** Collection tabs, in the order note 03 §3.2 records them. */
export const collectionTabs = [
  { id: "overview", label: "Overview" },
  { id: "recent", label: "Documents" },
  { id: "popular", label: "Popular" },
  { id: "updated", label: "Recently updated" },
  { id: "published", label: "Recently published" },
  { id: "old", label: "Least recently updated" },
  { id: "alphabetical", label: "A–Z" },
] as const;

export type CollectionTabId = (typeof collectionTabs)[number]["id"];

export function isCollectionTab(value: string | undefined): value is CollectionTabId {
  return collectionTabs.some((tab) => tab.id === value);
}

/**
 * The `documents.list` body each tab asks for. "Documents" honours the
 * collection's own stored sort, which is what makes a manually ordered
 * collection read in its manual order; the rest name their sort outright.
 */
export function collectionTabBody(
  tab: CollectionTabId,
  collection: Collection | undefined,
): DocumentListBody {
  const collectionId = collection?.id ?? "";
  switch (tab) {
    case "popular":
      return { collectionId, sort: "popularityScore", direction: "DESC" };
    case "updated":
      return { collectionId, sort: "updatedAt", direction: "DESC" };
    case "published":
      return { collectionId, sort: "publishedAt", direction: "DESC" };
    case "old":
      return { collectionId, sort: "updatedAt", direction: "ASC" };
    case "alphabetical":
      return { collectionId, sort: "title", direction: "ASC" };
    default: {
      const field = collection?.sort?.field ?? "index";
      const direction = (collection?.sort?.direction ?? "asc").toUpperCase();
      return {
        collectionId,
        sort: field as DocumentListBody["sort"],
        direction: direction === "ASC" ? "ASC" : "DESC",
      };
    }
  }
}

function MemberAvatars({ users }: { users: User[] }): ReactNode {
  if (users.length === 0) return null;
  return (
    <ul aria-label="Collection members" className="flex items-center -space-x-1">
      {users.slice(0, 6).map((user) => (
        <li
          key={user.id}
          title={user.name}
          className="flex h-6 w-6 items-center justify-center rounded-full border border-line bg-raised text-[10px] font-semibold"
        >
          {user.name.trim().charAt(0).toUpperCase()}
        </li>
      ))}
      {users.length > 6 && <li className="pl-2 text-xs text-muted">+{users.length - 6}</li>}
    </ul>
  );
}

export function CollectionScreen(): ReactNode {
  const { slug, tab } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const query = useCollection(slug);
  const { session } = useAuth();
  const collection = query.data?.collection;
  const abilities = query.data?.abilities ?? {};
  const active: CollectionTabId = isCollectionTab(tab) ? tab : "overview";

  const documents = useDocumentPage(
    "documents.list",
    collectionTabBody(active, collection),
    collection !== undefined,
  );
  const members = useCollectionMembers(collection?.id, collection !== undefined);

  const archive = useArchiveCollection();
  const remove = useDeleteCollection();
  const duplicate = useDuplicateCollection();

  const [editOpen, setEditOpen] = useState(false);
  const [permissionsOpen, setPermissionsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    writeStored(StorageKey.collectionTab, active);
  }, [active]);

  if (tab !== undefined && !isCollectionTab(tab)) {
    return <Navigate to="/404" replace />;
  }

  if (query.isPending) {
    return (
      <p role="status" className="text-sm text-muted">
        Loading collection…
      </p>
    );
  }

  if (query.isError || collection === undefined) {
    return (
      <div className="max-w-3xl">
        <h2 className="text-2xl font-semibold">Collection unavailable</h2>
        <p role="alert" className="mt-2 text-sm text-danger">
          {query.error?.message ?? "This collection could not be loaded."}
        </p>
      </div>
    );
  }

  const report = (error: Error): void => {
    toast.show(error.message, "error");
  };

  const overflow: MenuItem[] = [];
  if (granted(abilities, "update")) {
    overflow.push({
      label: "Edit…",
      onSelect: () => {
        setEditOpen(true);
      },
    });
    overflow.push({
      label: "Permissions…",
      onSelect: () => {
        setPermissionsOpen(true);
      },
    });
  }
  if (granted(abilities, "duplicate")) {
    overflow.push({
      label: "Duplicate",
      onSelect: () => {
        duplicate.mutate(
          { id: collection.id },
          {
            onSuccess: (copy) => {
              toast.show("Collection duplicated");
              void navigate(collectionPath(copy));
            },
            onError: report,
          },
        );
      },
    });
  }
  if (granted(abilities, "archive")) {
    overflow.push({
      label: "Archive",
      onSelect: () => {
        archive.mutate(
          { id: collection.id },
          {
            onSuccess: () => {
              toast.show("Collection archived");
            },
            onError: report,
          },
        );
      },
    });
  }
  if (granted(abilities, "delete")) {
    overflow.push({
      label: "Delete",
      destructive: true,
      onSelect: () => {
        setDeleteOpen(true);
      },
    });
  }

  return (
    <>
      <PageHeader>
        <h1 className="flex min-w-0 flex-1 items-center gap-2 truncate text-base font-semibold">
          <span aria-hidden="true" style={{ color: collection.color ?? undefined }}>
            {collection.icon ?? "📁"}
          </span>
          {collection.name}
        </h1>
        <div className="flex shrink-0 items-center gap-2">
          <MemberAvatars users={members.data?.users ?? []} />
          <Button
            size="sm"
            onClick={() => {
              setShareOpen(true);
            }}
          >
            Share
          </Button>
          {granted(abilities, "createDocument") && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                void navigate(`/doc/new?collectionId=${encodeURIComponent(collection.id)}`);
              }}
            >
              New document
            </Button>
          )}
          <Menu
            buttonLabel="Collection actions"
            menuLabel="Collection actions"
            items={overflow}
            trigger="⋯"
          />
        </div>
      </PageHeader>

      <div className="max-w-3xl">
        <h2 className="flex items-center gap-2 text-2xl font-semibold">
          <span aria-hidden="true" style={{ color: collection.color ?? undefined }}>
            {collection.icon ?? "📁"}
          </span>
          {collection.name}
        </h2>

        <nav
          aria-label="Collection tabs"
          className="mt-4 flex flex-wrap gap-1 border-b border-line"
        >
          {collectionTabs.map((entry) => (
            <NavLink
              key={entry.id}
              to={`/collection/${slug ?? ""}/${entry.id}`}
              className={({ isActive }) =>
                cx(
                  "-mb-px border-b-2 px-3 py-2 text-sm",
                  isActive
                    ? "border-accent text-ink"
                    : "border-transparent text-muted hover:text-ink",
                )
              }
            >
              {entry.label}
            </NavLink>
          ))}
        </nav>

        {active === "overview" && (
          <section aria-label="Overview" className="mt-4">
            {granted(abilities, "update") && (
              <div className="mb-2 flex justify-end">
                <Button
                  size="sm"
                  onClick={() => {
                    void navigate(`/collection/${slug ?? ""}/overview/edit`);
                  }}
                >
                  Edit overview
                </Button>
              </div>
            )}
            {collection.description === null ||
            collection.description === undefined ||
            collection.description === "" ? (
              <p className="text-sm text-muted">This collection has no description yet.</p>
            ) : (
              <LazyMarkdown value={collection.description} />
            )}
          </section>
        )}

        <DocumentList
          label={collectionTabs.find((entry) => entry.id === active)?.label ?? "Documents"}
          documents={documents.data?.documents ?? []}
          abilities={documents.data?.abilities ?? {}}
          collections={[collection]}
          loading={documents.isPending}
          error={documents.error}
          emptyMessage="This collection has no documents yet."
        />
      </div>

      <EditCollectionDialog
        open={editOpen}
        collection={collection}
        onClose={() => {
          setEditOpen(false);
        }}
      />
      <ShareDialog
        open={shareOpen}
        title={collection.name}
        target={{ collectionId: collection.id }}
        canShare={granted(abilities, "share")}
        sharingEnabled={session?.info.team.sharing !== false && collection.sharing !== false}
        onClose={() => {
          setShareOpen(false);
        }}
      />
      <CollectionPermissionsDialog
        open={permissionsOpen}
        collection={collection}
        onClose={() => {
          setPermissionsOpen(false);
        }}
      />
      <ConfirmDialog
        open={deleteOpen}
        title="Delete collection"
        description={`“${collection.name}” and every document in it are deleted. This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        busy={remove.isPending}
        onClose={() => {
          setDeleteOpen(false);
        }}
        onConfirm={() => {
          remove.mutate(
            { id: collection.id },
            {
              onSuccess: () => {
                setDeleteOpen(false);
                toast.show("Collection deleted");
                void navigate("/home");
              },
              onError: report,
            },
          );
        }}
      />
    </>
  );
}

/** `/collection/:slug` sends the visitor to whichever tab they used last. */
export function CollectionRedirect(): ReactNode {
  const { slug } = useParams();
  const remembered = readStored(StorageKey.collectionTab);
  const tab = isCollectionTab(remembered ?? undefined) ? remembered : "overview";
  return <Navigate to={`/collection/${slug ?? ""}/${tab ?? "overview"}`} replace />;
}
